import { takeEvery } from 'redux-saga';
import { take, put, call, fork, select } from 'redux-saga/effects';

import {
    CANCEL_BOOKING,
    CHECK_OUT_BOOKING,
    CHECK_IN_BOOKING,
    UPDATE_BOOKING_TIMES,
    FETCH_UNAVAILABLE_TIMES,
    DATE_TIME_FORMAT,
    EDIT_BOOKING_TIME_CLICKED,
} from '../constants';

import {
    UNAVAILABLE_TIMES_DAYS_PRIOR,
    UNAVAILABLE_TIMES_DAYS_FOLLOWING,
} from '../constants/bookingUnavailability';

import { CONFLICT } from '../constants/httpStatusCodes';

import {
    BOOKING_TIME_CONFLICT,
    ENTITY_OUT_OF_DATE_EXCEPTION
} from '../constants/apiErrorCodes';

import {
    BOOKING_STATUS_CHECKED_OUT,
    BOOKING_STATUS_CHECKED_IN,
    BOOKING_STATUS_CANCELLED,
} from '../constants/booking';

import * as loadingGraphics from '../constants/loading';

import { getBooking, getCurrentAvailabilityWindow } from '../selectors';
import * as api from '../services/api';
import * as environmentActions from '../actions/environment';
import * as entitiesActions from '../actions/entities';
import { bookingUpdated, cancelBooking } from '../actions/bookings';
import * as bookingHelpers from '../util/bookingHelpers';
import { callApi, getConflictReasonText } from './common';
import { forcePollEntities } from './entities';
import { getBookingByVersionedUId } from '../selectors';

function* handleBookingUpdateError(error) {
    if (error.status === CONFLICT) {
        error.handled = true;
        if (error.body.errorCode === ENTITY_OUT_OF_DATE_EXCEPTION) {
            yield fork(forcePollEntities);
            yield put(environmentActions.setError([`The booking you are trying to update has been modified elsewhere.`, `We'll fetch the latest for you.`]));
        }
    }
}

function* watchCancelBooking() {
    while(true) {
        const { id } = yield take(CANCEL_BOOKING);
        const response = yield call(callApi, {
            apiMethod: api.setBookingState,
            id,
            apiData: { statusId: BOOKING_STATUS_CANCELLED },
            loading: {
                graphic: loadingGraphics.GENERIC,
                loadingText: `Cancelling booking...`,
            },
            postSnackbarText: `Your booking has been cancelled.`,
            onError: handleBookingUpdateError,
        });

        if (response.ok) {
            yield put(bookingUpdated(response.body));
        }
    }
}

function* watchCheckOutBooking() {
    while(true) {
        const { id } = yield take(CHECK_OUT_BOOKING);
        const response = yield call(callApi, {
            apiMethod: api.setBookingState,
            id,
            apiData: { statusId: BOOKING_STATUS_CHECKED_OUT },
            loading: {
                graphic: loadingGraphics.CHECK_OUT,
                loadingText: `Checking out booking...`,
                successText: `Booking checked out.`,
            },
            onError: handleBookingUpdateError,
        });

        if (response.ok) {
            yield put(bookingUpdated(response.body));
        }
    }
}

function* watchCheckInBooking() {
    while(true) {
        const { id } = yield take(CHECK_IN_BOOKING);
        const response = yield call(callApi, {
            apiMethod: api.setBookingState,
            id,
            apiData: { statusId: BOOKING_STATUS_CHECKED_IN },
            loading: {
                graphic: loadingGraphics.CHECK_IN,
                loadingText: `Checking in booking...`,
                successText: `Booking checked in.`,
            },
            onError: handleBookingUpdateError,
        });

        if (response.ok) {
            yield put(bookingUpdated(response.body));
        }
    }
}

function* watchUpdateBookingTimes() {
    while(true) {
        const { versionedUId, staticUId, startTime, endTime } = yield take(UPDATE_BOOKING_TIMES);
        const response = yield call(callApi, {
            apiMethod: api.setBookingTimes,
            id: versionedUId,
            apiData: {
                startTime: startTime ? startTime.format(DATE_TIME_FORMAT) : null,
                endTime: endTime ? endTime.format(DATE_TIME_FORMAT) : null,
            },
            loading: {
                graphic: loadingGraphics.GENERIC,
                loadingText: `Updating booking...`,
                successText: `Booking updated.`,
                successGraphic: loadingGraphics.GENERIC_DONE,
            },
            onError: handleBookingTimeConflictError,
        });

        if (response.ok) {
            yield put(bookingUpdated(response.body));
        }
    }
}

function* handleBookingTimeConflictError(response, versionedUId) {
    // TODO: Can we do this without a selector?
    const booking = yield select(getBookingByVersionedUId, versionedUId);
    const staticUId = booking.staticUId;

    if (response.status === CONFLICT && response.body.errorCode === BOOKING_TIME_CONFLICT) {
        response.handled = true;
        // Fetch updated unavailable times
        yield fork(fetchUnavailableTimes, versionedUId, staticUId, false);

        // Display error with reasons for conflict
        const conflictText = getConflictReasonText(response.body.data.reasons);
        yield put(environmentActions.setError([
            `The selected times are unavailable:`,
            conflictText,
            `We'll update the available times.`
        ]));
    }
}

function* fetchUnavailableTimes(versionedUId, staticUId, showLoading) {
    const booking = yield select(getBooking, staticUId);
    const bookingTimes = bookingHelpers.getStartEndTimesBasedOnBookingStatus(booking);

    const startDate = bookingTimes.start
        .startOf(`day`).subtract(UNAVAILABLE_TIMES_DAYS_PRIOR, `days`)
        .format(DATE_TIME_FORMAT);
    const endDate = bookingTimes.end
        .startOf(`day`).add(UNAVAILABLE_TIMES_DAYS_FOLLOWING, `days`)
        .format(DATE_TIME_FORMAT);

    let loading;
    if (showLoading) {
        loading = {
            graphic: loadingGraphics.GENERIC,
            loadingText: `Checking availability...`,
        };
    }

    const response = yield call(callApi, {
        apiMethod: api.fetchUnavailableTimes,
        id: versionedUId,
        apiQuery: {
            startDate,
            endDate,
        },
        loading,
    });

    if (response.ok) {
        yield put.sync(entitiesActions.setUnavailableTimes(staticUId, response.body));
        const currentAvailabilityWindow = yield select(getCurrentAvailabilityWindow, staticUId);

        if (!currentAvailabilityWindow.valid) {
            yield put(environmentActions.setError([
                `There are conflicts with other Bookings which prevent any changes being made to this Booking's start/end times`
            ]));
        }
    }
}

function* watchFetchUnavailableTimes() {
    while(true) {
        const { versionedUId, staticUId, showLoading } = yield take(FETCH_UNAVAILABLE_TIMES);
        yield call(fetchUnavailableTimes, versionedUId, staticUId, showLoading);
    }
}

function* watchClickEditBookingTime() {
    yield takeEvery(EDIT_BOOKING_TIME_CLICKED, function*(data) {
        const { staticUId, versionedUId } = data;

        // Fetch updated unavailable times
        yield fork(fetchUnavailableTimes, versionedUId, staticUId, true);
    });
}

export {
    watchCancelBooking,
    watchCheckOutBooking,
    watchCheckInBooking,
    watchUpdateBookingTimes,
    watchFetchUnavailableTimes,
    watchClickEditBookingTime,
};



// WEBPACK FOOTER //
// ./scripts/sagas/booking.js